import { Link } from 'react-router-dom';
import { Zap, Map, FileText, Layers, GitBranch, Compass } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[var(--bg-primary)] relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute w-full h-full">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-[var(--accent-primary)]"
                />
              </pattern>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
        <div className="absolute w-[800px] h-[800px] -top-[400px] -left-[400px] bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute w-[600px] h-[600px] -bottom-[300px] -right-[300px] bg-purple-500/20 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-16">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-4 mb-8">
              <h1 className="text-7xl font-bold text-white tracking-tight">
                GridMap
              </h1>
              <Zap className="text-[var(--accent-primary)] w-16 h-16" />
            </div>
            <p className="text-[var(--text-secondary)] text-xl mb-12 max-w-2xl mx-auto">
              Transform your power distribution projects with intelligent mapping, comprehensive project management, and real-time collaboration.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/signup"
                className="btn btn-primary text-lg px-8 py-4 rounded-lg transform transition hover:scale-105 flex items-center justify-center gap-2 bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-secondary)]"
              >
                <span>Start Mapping</span>
                <Map className="w-5 h-5" />
              </Link>
              <Link
                to="/signin"
                className="btn btn-secondary text-lg px-8 py-4 rounded-lg transform transition hover:scale-105 flex items-center justify-center gap-2 border-2 border-[var(--accent-primary)] text-[var(--accent-primary)] hover:bg-[var(--accent-primary)] hover:text-white"
              >
                <span>Sign In</span>
                <Compass className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {/* Interactive Mapping */}
            <div className="bg-[var(--bg-secondary)]/50 backdrop-blur-sm p-8 rounded-2xl border border-[var(--border-color)] transform transition hover:-translate-y-1">
              <div className="w-14 h-14 mb-6 bg-[var(--accent-primary)]/20 rounded-xl flex items-center justify-center">
                <Map className="w-8 h-8 text-[var(--accent-primary)]" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Interactive Mapping</h3>
              <p className="text-[var(--text-secondary)]">
                Create detailed power distribution maps with precision. Add equipment, draw conductors, and visualize your entire network in real-time.
              </p>
            </div>

            {/* Field Notes */}
            <div className="bg-[var(--bg-secondary)]/50 backdrop-blur-sm p-8 rounded-2xl border border-[var(--border-color)] transform transition hover:-translate-y-1">
              <div className="w-14 h-14 mb-6 bg-[var(--accent-primary)]/20 rounded-xl flex items-center justify-center">
                <FileText className="w-8 h-8 text-[var(--accent-primary)]" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Field Notes</h3>
              <p className="text-[var(--text-secondary)]">
                Document specifications, measurements, and observations directly on your maps. Keep all project information organized and accessible.
              </p>
            </div>

            {/* Equipment Management */}
            <div className="bg-[var(--bg-secondary)]/50 backdrop-blur-sm p-8 rounded-2xl border border-[var(--border-color)] transform transition hover:-translate-y-1">
              <div className="w-14 h-14 mb-6 bg-[var(--accent-primary)]/20 rounded-xl flex items-center justify-center">
                <Layers className="w-8 h-8 text-[var(--accent-primary)]" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Equipment Management</h3>
              <p className="text-[var(--text-secondary)]">
                Track poles, transformers, switches, and more. Manage equipment specifications and maintenance schedules efficiently.
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to revolutionize your power distribution projects?
            </h2>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 bg-[var(--accent-primary)] text-white px-8 py-4 rounded-lg transform transition hover:scale-105 hover:bg-[var(--accent-secondary)]"
            >
              <span>Get Started Now</span>
              <GitBranch className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage; 